import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import GameLeaderboards from './GameLeaderboards';
import TournamentList from '../../../components/Tournaments/TournamentList';
import AchievementList from '../../../components/Achievements/AchievementList';

const GameTabs = ({ game, userReview, onSubmitReview }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedScreenshot, setSelectedScreenshot] = useState(null);
  const [reviewText, setReviewText] = useState('');
  const [reviewRating, setReviewRating] = useState(5);

  // Update form when userReview changes
  React.useEffect(() => {
    if (userReview) {
      setReviewText(userReview.comment || userReview.review_text || '');
      setReviewRating(userReview.rating || 5);
    }
  }, [userReview]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Info' },
    { id: 'leaderboards', label: 'Leaderboards', icon: 'Trophy' },
    { id: 'tournaments', label: 'Tournaments', icon: 'Award' },
    { id: 'achievements', label: 'Achievements', icon: 'Star' },
    { id: 'screenshots', label: 'Screenshots', icon: 'Image' },
    { id: 'reviews', label: 'Reviews', icon: 'MessageSquare' }
  ];

  const handleSubmitReview = async () => {
    if (reviewText.trim()) {
      try {
        const result = await onSubmitReview({
          rating: reviewRating,
          comment: reviewText,
          date: new Date().toISOString()
        });
        if (result) {
          setReviewText('');
          setReviewRating(5);
        }
      } catch (error) {
        console.error('Failed to submit review:', error);
      }
    }
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Description */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-3">About This Game</h3>
        <p className="text-muted-foreground leading-relaxed">{game.description}</p>
      </div>

      {/* Game Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="text-md font-semibold text-foreground mb-3">Game Details</h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Developer</span>
              <span className="text-foreground">{game?.gaming_studios?.name || game?.studio || '—'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Release Date</span>
              <span className="text-foreground">
                {game?.published_at ? new Date(game.published_at).toLocaleDateString() : game?.releaseDate || '—'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Genre</span>
              <span className="text-foreground">
                {game?.game_category_mappings?.length > 0 
                  ? game.game_category_mappings.map(m => m.game_categories.name).join(', ')
                  : game?.category || '—'
                }
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Avg Play Time</span>
              <span className="text-foreground">
                {game?.estimated_play_time 
                  ? `${Math.floor(game.estimated_play_time / 60)}h ${game.estimated_play_time % 60}m`
                  : game?.playtime || '—'
                }
              </span>
            </div>
          </div>
        </div>

        <div>
          <h4 className="text-md font-semibold text-foreground mb-3">System Requirements</h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">WebGPU Support</span>
              <span className="text-accent">Required</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Browser</span>
              <span className="text-foreground">Chrome 113+</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">RAM</span>
              <span className="text-foreground">4GB minimum</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Storage</span>
              <span className="text-foreground">2GB available</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tags */}
      {game.tags && game.tags.length > 0 && (
        <div>
          <h4 className="text-md font-semibold text-foreground mb-3">Tags</h4>
          <div className="flex flex-wrap gap-2">
            {game.tags.map((tag, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-muted text-muted-foreground text-sm rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderScreenshots = () => (
    <div className="space-y-4">
      {game.screenshots && game.screenshots.length > 0 ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {game.screenshots.map((screenshot, index) => (
              <div
                key={index}
                className="relative aspect-video cursor-pointer group overflow-hidden rounded-lg"
                onClick={() => setSelectedScreenshot(screenshot)}
              >
                <Image
                  src={screenshot.url}
                  alt={`${game.title} Screenshot ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200 flex items-center justify-center">
                  <Icon name="Expand" size={24} className="opacity-0 group-hover:opacity-100 transition-opacity text-white" />
                </div>
              </div>
            ))}
          </div>

          {/* Screenshot Modal */}
          {selectedScreenshot && (
            <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
              <div className="relative max-w-4xl max-h-full">
                <Image
                  src={selectedScreenshot.url}
                  alt={selectedScreenshot.caption}
                  className="max-w-full max-h-full object-contain"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="X"
                  onClick={() => setSelectedScreenshot(null)}
                  className="absolute top-4 right-4 bg-black/50 text-white hover:bg-black/70"
                />
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-8">
          <Icon name="Image" size={48} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No screenshots available</p>
        </div>
      )}
    </div>
  );

  const renderReviews = () => (
    <div className="space-y-6">
      {/* Review Summary */}
      <div className="bg-muted/30 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-foreground">Player Reviews</h4>
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1">
              <Icon name="Star" size={20} className="text-yellow-500 fill-current" />
              <span className="text-lg font-semibold">{game.rating || game.average_rating?.toFixed(1) || 'N/A'}</span>
            </div>
            <span className="text-muted-foreground">({game.reviewCount || game.reviews?.length || 0} reviews)</span>
          </div>
        </div>
        
        {game.ratingDistribution && (
          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map((stars) => (
              <div key={stars} className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground w-8">{stars}★</span>
                <div className="flex-1 bg-muted rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full" 
                    style={{ width: `${game.ratingDistribution[stars] || 0}%` }}
                  />
                </div>
                <span className="text-sm text-muted-foreground w-12">
                  {game.ratingDistribution[stars] || 0}%
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* User's Existing Review */}
      {userReview && (
        <div className="bg-card border border-border rounded-lg p-4">
          <h4 className="text-md font-semibold text-foreground mb-3">Your Review</h4>
          <div className="space-y-3">
            <div className="flex items-center space-x-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Icon
                  key={star}
                  name="Star"
                  size={16}
                  className={star <= userReview.rating ? 'text-yellow-500 fill-current' : 'text-muted-foreground'}
                />
              ))}
              <span className="ml-2 text-sm text-muted-foreground">
                {new Date(userReview.date || userReview.created_at).toLocaleDateString()}
              </span>
            </div>
            <p className="text-sm text-foreground">{userReview.comment || userReview.review_text}</p>
          </div>
        </div>
      )}

      {/* Write Review */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h4 className="text-md font-semibold text-foreground mb-3">
          {userReview ? 'Update Your Review' : 'Write a Review'}
        </h4>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Rating</label>
            <div className="flex items-center space-x-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setReviewRating(star)}
                  className={`p-1 ${star <= reviewRating ? 'text-yellow-500' : 'text-muted-foreground'}`}
                >
                  <Icon name="Star" size={20} className={star <= reviewRating ? 'fill-current' : ''} />
                </button>
              ))}
            </div>
          </div>
          
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Your Review</label>
            <textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="Share your thoughts about this game..."
              className="w-full h-24 px-3 py-2 bg-input border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            />
          </div>
          
          <Button
            variant="default"
            size="sm"
            onClick={handleSubmitReview}
            disabled={!reviewText.trim()}
          >
            {userReview ? 'Update Review' : 'Submit Review'}
          </Button>
        </div>
      </div>

      {/* Recent Reviews */}
      <div className="space-y-4">
        <h4 className="text-md font-semibold text-foreground">Recent Reviews</h4>
        {game.reviews && game.reviews.length > 0 ? (
          game.reviews.map((review, index) => (
            <div key={review.id || index} className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                    <Icon name="User" size={16} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{review.username || 'Anonymous'}</p>
                    <div className="flex items-center space-x-1">
                      {[...Array(5)].map((_, i) => (
                        <Icon
                          key={i}
                          name="Star"
                          size={12}
                          className={i < review.rating ? 'text-yellow-500 fill-current' : 'text-muted-foreground'}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground">{review.date}</span>
              </div>
              <p className="text-sm text-muted-foreground">{review.comment || review.review_text}</p>
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <Icon name="MessageSquare" size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No reviews yet</p>
            <p className="text-sm text-muted-foreground">Be the first to review this game!</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Tab Navigation */}
      <div className="border-b border-border">
        <div className="flex space-x-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium transition-colors border-b-2 ${
                activeTab === tab.id
                  ? 'text-primary border-primary' :'text-muted-foreground border-transparent hover:text-foreground'
              }`}
            >
              <Icon name={tab.icon} size={16} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'leaderboards' && <GameLeaderboards gameId={game.id} />}
        {activeTab === 'tournaments' && <TournamentList gameId={game.id} />}
        {activeTab === 'achievements' && <AchievementList gameId={game.id} showFilter={true} />}
        {activeTab === 'screenshots' && renderScreenshots()}
        {activeTab === 'reviews' && renderReviews()}
      </div>
    </div>
  );
};

export default GameTabs;
